import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type FoodOrderEventView, type FoodOrderStatus, type FoodOrderView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, Drawer, EmptyState, ListTable, Notice, PageHeader, Panel, SegmentedControl, type Column, DrawerHeader, DrawerBody, DrawerFooter, SectionTitle, Text, Ink, Line, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  FOOD_CLOSED_STATUSES,
  FOOD_WORKLIST_STATUSES,
  OPS_FOOD_CANCEL_REASONS,
  foodOrderCancellable,
  foodOrderInScope,
  marketsOfRegion,
  REGION_READ_MAX_MARKETS,
  foodOrderMarket,
  foodOrderNeedsAttention,
  foodOrderTone,
  formatMarketInstant,
  marketTimeZone,
  mayCancelFoodOrders,
  sortFoodOrders,
  tailOf,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { LoadingBlock } from '../components/loading-block';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';

type Filter = 'open' | 'closed';

export function FoodOrdersScreen(): ReactElement {
  const { runtime, scope, markets, grants } = useAdmin();
  const { toast, confirm } = useConsole();
  const { t, tp, lang } = useT();
  const { fail, v, has } = useVocab();

  // Review W6N-08: the cancel route is `order:manage`; the worklist is `order:view` (SUPPORT and MERCHANT_OPS hold only
  // the read), so the action is offered only to an operator the server would let cancel.
  const mayCancel = mayCancelFoodOrders(grants);
  const [filter, setFilter] = useState<Filter>('open');
  const [orders, setOrders] = useState<readonly FoodOrderView[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [listError, setListError] = useState<string | undefined>(undefined);
  const [openOrder, setOpenOrder] = useState<FoodOrderView | undefined>(undefined);
  const [events, setEvents] = useState<readonly FoodOrderEventView[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  // Only the latest read may land (a filter switch or a poll can overtake an older answer).
  const readSeq = useRef(0);

  const statuses = useMemo<readonly FoodOrderStatus[]>(() => (filter === 'open' ? FOOD_WORKLIST_STATUSES : FOOD_CLOSED_STATUSES), [filter]);

  // D420: the scoped city travels to the server; without one the platform page is read and narrowed below.
  const city = scope.current.city;
  // ADR-0124: a state has no server-side filter (and its grant does not cover the platform page), so it is read market by market and merged.
  const region = city === undefined ? scope.current.region : undefined;
  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    const stateMarkets = region !== undefined ? marketsOfRegion(region, markets).map((m) => m.id).slice(0, REGION_READ_MAX_MARKETS) : undefined;
    const reads = stateMarkets !== undefined ? stateMarkets.map((market) => runtime.food.list({ statuses, market })) : [runtime.food.list({ statuses, ...(city !== undefined ? { market: city } : {}) })];
    const answers = await Promise.all(reads);
    if (seq !== readSeq.current) return;
    const failed = answers.find((a) => !a.ok);
    const res = failed ?? (answers.length === 1 ? answers[0] : undefined);
    if (res === undefined) {
      const ok = answers.flatMap((a) => (a.ok ? [a.value] : []));
      setOrders(ok.flatMap((v) => v.orders));
      setTruncated(ok.some((v) => v.truncated === true));
      setListError(undefined);
    } else if (res.ok) {
      setOrders(res.value.orders);
      setTruncated(res.value.truncated === true);
      setListError(undefined);
    } else {
      setOrders((prev) => prev ?? []);
      setListError(res.error.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('w6.refused', { code: fail(res.error) }));
    }
  }, [runtime, statuses, city, region, markets, t]);

  useEffect(() => {
    setOrders(undefined);
    void load();
  }, [load]);

  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.live });

  // The scoped city (or country, W6N-13) narrows the page the server answered; the note above the table says the narrowing is the
  // console's, not the server's.
  const current = scope.current;
  const rows = useMemo(() => sortFoodOrders(orders ?? []).filter((o) => foodOrderInScope(o, current, markets)), [orders, current, markets]);
  const hidden = (orders?.length ?? 0) - rows.length;
  const attention = rows.filter(foodOrderNeedsAttention).length;

  const money = useCallback(
    (m: { readonly amountMinor: string; readonly currency: string } | undefined): string => {
      if (m === undefined) return '—';
      return /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency)
        ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' })
        : `${m.amountMinor} ${m.currency}`;
    },
    [lang],
  );

  const zoneOf = (order: FoodOrderView): string | undefined => order.timezone ?? marketTimeZone(foodOrderMarket(order), markets);
  const labelOf = (order: FoodOrderView): string => order.code ?? tailOf(order.orderId);

  const openDetail = async (order: FoodOrderView): Promise<void> => {
    setOpenOrder(order);
    setEvents(undefined);
    const res = await runtime.food.get(order.orderId);
    if (res.ok) {
      setOpenOrder(res.value.order);
      setEvents(res.value.events);
    } else {
      setEvents([]);
    }
  };

  const cancel = async (order: FoodOrderView): Promise<void> => {
    const answer = await confirm({
      title: t('forders.cancel.title', { order: labelOf(order) }),
      body: t('forders.cancel.body'),
      confirmLabel: t('forders.cancel.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: 'danger',
      icon: 'ban',
      reasons: OPS_FOOD_CANCEL_REASONS.map((code) => v('food.ops_cancel_reason', code)),
      reasonLabel: t('w6.reason.label'),
      reasonsOnly: true,
    });
    if (!answer.confirmed || answer.reason === undefined) return;
    const reasonCode = OPS_FOOD_CANCEL_REASONS.find((code) => v('food.ops_cancel_reason', code) === answer.reason);
    if (reasonCode === undefined) return;
    setBusy(true);
    const res = await runtime.food.cancel(order.orderId, 'ops', reasonCode);
    setBusy(false);
    if (res.ok) {
      toast({ message: t('forders.cancel.done', { order: labelOf(order) }), tone: 'success' });
      setOpenOrder(res.value.order);
      await load();
      return;
    }
    toast({ message: t('w6.actionRefused', { code: fail(res.error) }), tone: 'warning' });
  };

  const columns: readonly Column<FoodOrderView>[] = [
    { key: 'code', header: t('forders.col.code'), width: 'minmax(96px, 0.8fr)', render: (o) => <span className="tnum">{labelOf(o)}</span> },
    { key: 'store', header: t('forders.col.store'), width: 'minmax(96px, 0.9fr)', render: (o) => o.storeName !== undefined && o.storeName !== null && o.storeName !== '' ? <span title={o.storeName}>{o.storeName}</span> : o.merchant?.locationId !== undefined ? <IdRef value={o.merchant.locationId} /> : <span>—</span> },
    { key: 'status', header: t('forders.col.status'), width: 'minmax(140px, 1fr)', render: (o) => <Badge tone={foodOrderTone(o.status)}>{v('food.order_status', o.status)}</Badge> },
    { key: 'channel', header: t('forders.col.channel'), width: 'minmax(120px, 0.8fr)', render: (o) => t(o.channel === 'partner_desk' ? 'forders.channel.partner_desk' : 'forders.channel.app') },
    { key: 'total', header: t('forders.col.total'), width: 'minmax(100px, 0.8fr)', render: (o) => <span className="tnum">{money(o.totals?.total)}</span> },
    {
      key: 'placed',
      header: t('forders.col.placed'),
      width: 'minmax(140px, 1fr)',
      render: (o) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(o.createdAt ?? o.updatedAt, zoneOf(o), lang)}</Text>,
    },
  ];

  const detailZone = openOrder !== undefined ? zoneOf(openOrder) : undefined;

  return (
    <>
      <PageHeader title={t('forders.title')} subtitle={t('forders.subtitle')} />

      <Panel>
        <SegmentedControl
          ariaLabel={t('forders.filter.aria')}
          value={filter}
          onChange={setFilter}
          options={[
            { id: 'open', label: t('forders.filter.open') },
            { id: 'closed', label: t('forders.filter.closed') },
          ]}
        />
      </Panel>

      {attention > 0 && (
        <div>
          <Notice tone="warning">
            <span className="tnum">{tp('forders.attention', attention)}</span>
          </Notice>
        </div>
      )}
      {hidden > 0 && (
        <div>
          <Notice tone="neutral">{t('forders.marketNote', { n: String(hidden) })}</Notice>
        </div>
      )}
      {truncated && (
        <div>
          <Notice tone="neutral">{t('forders.truncated')}</Notice>
        </div>
      )}

      <ListTable
        columns={columns}
        rows={rows}
        rowKey={(o) => o.orderId}
        onRowClick={(o) => void openDetail(o)}
        selectedKey={openOrder?.orderId}
        loading={orders === undefined}
        loadingLabel={t('ui.loading')}
        error={listError}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('forders.empty.title')} detail={t('forders.empty.detail')} />}
        minWidth={960}
      />

      <Drawer open={openOrder !== undefined} onClose={() => setOpenOrder(undefined)} labelledBy="forders-detail-title">
        {openOrder !== undefined && (
          <>
            <DrawerHeader titleId="forders-detail-title" title={t('forders.detail.title', { order: labelOf(openOrder) })} subtitle={<Badge tone={foodOrderTone(openOrder.status)}>{v('food.order_status', openOrder.status)}</Badge>} />
            <DrawerBody>
              <DrawerStack>
                {openOrder.rejectionReason !== undefined && <Notice tone="danger">{t('forders.rejection', { reason: v('food.merchant_rejection_reason', openOrder.rejectionReason) })}</Notice>}
                {openOrder.cancellation !== undefined && (
                  <Notice tone="danger">{t('forders.cancellation', { by: v('food.cancelled_by', openOrder.cancellation.by), reason: v(['food.cancel_reason', 'food.merchant_rejection_reason', 'food.ops_cancel_reason'].find((id) => has(id, openOrder.cancellation?.reasonCode)) ?? 'food.cancel_reason', openOrder.cancellation.reasonCode) })}</Notice>
                )}

                <section>
                  <SectionTitle level={3} size="sub">{t('forders.detail.lines')}</SectionTitle>
                  {(openOrder.lines ?? []).length === 0 ? (
                    <Text as="p" role="caption" tone="secondary">—</Text>
                  ) : (
                    (openOrder.lines ?? []).map((line) => (
                      <Line key={line.lineId} ruled caption>
                        <span>
                          <span className="tnum">{line.quantity}×</span> {line.name}
                        </span>
                        <span className="tnum">{money(line.lineTotal)}</span>
                      </Line>
                    ))
                  )}
                </section>

                <section>
                  <SectionTitle level={3} size="sub">{t('forders.detail.totals')}</SectionTitle>
                  {openOrder.totals === undefined ? (
                    <Text as="p" role="caption" tone="secondary">{t('forders.detail.noTotals')}</Text>
                  ) : (
                    (['subtotal', 'fees', 'discount', 'tax', 'tip', 'total'] as const).map((key) => (
                      <Line key={key} caption>
                        <Ink tone="secondary">{v('food.totals_component', key)}</Ink>
                        <Ink mono strong={key === 'total'}>{money(openOrder.totals?.[key])}</Ink>
                      </Line>
                    ))
                  )}
                </section>

                <section>
                  <SectionTitle level={3} size="sub">{t('forders.detail.events')}</SectionTitle>
                  {events === undefined ? (
                    <LoadingBlock lines={2} />
                  ) : (
                    events.map((e) => (
                      <Line key={e.eventId} caption>
                        <span>{e.toStatus !== undefined ? v('food.order_status', e.toStatus as FoodOrderStatus) : v('food.order_event_type', e.type)}</span>
                        <Text role="caption" tone="secondary" mono>{formatMarketInstant(e.at, detailZone, lang)}</Text>
                      </Line>
                    ))
                  )}
                </section>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setOpenOrder(undefined)}>{t('ui.close')}</Button>
              <span className="vello-grow" />
              {foodOrderCancellable(openOrder) && mayCancel && (
                <Button variant="danger" icon="ban" disabled={busy} onClick={() => void cancel(openOrder)}>
                  {t('forders.cancel.action')}
                </Button>
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
